import { forwardRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Settings, UserPlus, Sun, Moon, Monitor, Languages, ClipboardList, BarChart3 } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { changeAppLanguage } from '../lib/language';
import { cn } from '../lib/utils';
import type { Theme } from '../lib/theme';

interface NavbarMenuProps {
  /** Called when an item is selected so the parent can close the menu. */
  onItemSelect: () => void;
}

/**
 * Renders the menu contents (theme picker, language picker, Contact, Invite).
 * The parent owns the open/closed state and positioning.
 */
const NavbarMenu = forwardRef<HTMLDivElement, NavbarMenuProps>(function NavbarMenu(
  { onItemSelect },
  ref,
) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { isAuthenticated } = useAuth();

  const language: 'en' | 'it' = i18n?.language?.startsWith?.('it') ? 'it' : 'en';
  const setLanguage = (lang: 'en' | 'it') => {
    if (lang !== language) void changeAppLanguage(lang);
  };

  const itemClass = cn(
    'flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm',
    'transition-colors hover:bg-muted focus:bg-muted focus:outline-none',
  );

  return (
    <div
      ref={ref}
      role="menu"
      className={cn(
        'rounded-xl border bg-popover p-2 text-popover-foreground shadow-lg',
        'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2',
      )}
    >
      {/* Theme */}
      <div className="px-2 pt-1 pb-2">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
          {t('navbar.theme')}
        </p>
        <div className="grid grid-cols-3 gap-1 rounded-md border p-1">
          <ThemeButton current={theme} value="light" onSelect={setTheme} icon={<Sun className="h-3.5 w-3.5" />} label={t('navbar.lightMode')} />
          <ThemeButton current={theme} value="dark" onSelect={setTheme} icon={<Moon className="h-3.5 w-3.5" />} label={t('navbar.darkMode')} />
          <ThemeButton current={theme} value="system" onSelect={setTheme} icon={<Monitor className="h-3.5 w-3.5" />} label={t('navbar.systemMode')} />
        </div>
      </div>

      <div className="my-1 h-px bg-border" />

      {/* Language */}
      <div className="px-2 pt-1 pb-2">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
          <Languages className="h-3 w-3" />
          {t('profile.language')}
        </p>
        <div className="grid grid-cols-2 gap-1 rounded-md border p-1">
          <LanguageButton current={language} value="en" onSelect={setLanguage} flag="🇬🇧" label="English" />
          <LanguageButton current={language} value="it" onSelect={setLanguage} flag="🇮🇹" label="Italiano" />
        </div>
      </div>

      <div className="my-1 h-px bg-border" />

      {/* Links */}
      {isAuthenticated && (
        <Link to="/training-plans" className={itemClass} role="menuitem" onClick={onItemSelect}>
          <ClipboardList className="h-4 w-4 text-muted-foreground" />
          {t('navbar.trainingPlans')}
        </Link>
      )}

      {isAuthenticated && (
        <Link to="/my-stats" className={itemClass} role="menuitem" onClick={onItemSelect}>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          {t('navbar.myStats')}
        </Link>
      )}

      {isAuthenticated && (
        <Link to="/profile" className={itemClass} role="menuitem" onClick={onItemSelect}>
          <Settings className="h-4 w-4 text-muted-foreground" />
          {t('navbar.settings')}
        </Link>
      )}

      <Link to="/contact" className={itemClass} role="menuitem" onClick={onItemSelect}>
        <Mail className="h-4 w-4 text-muted-foreground" />
        {t('navbar.contact')}
      </Link>

      {isAuthenticated && (
        <Link to="/invite" className={itemClass} role="menuitem" onClick={onItemSelect}>
          <UserPlus className="h-4 w-4 text-muted-foreground" />
          {t('navbar.invite')}
        </Link>
      )}
    </div>
  );
});

export default NavbarMenu;

interface ThemeButtonProps {
  current: Theme;
  value: Theme;
  onSelect: (theme: Theme) => void;
  icon: ReactNode;
  label: string;
}

function ThemeButton({ current, value, onSelect, icon, label }: ThemeButtonProps) {
  const isActive = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={isActive}
      className={cn(
        'flex flex-col items-center justify-center gap-1 rounded-sm py-1.5 text-[10px] font-medium',
        'transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

interface LanguageButtonProps {
  current: 'en' | 'it';
  value: 'en' | 'it';
  onSelect: (lang: 'en' | 'it') => void;
  flag: string;
  label: string;
}

function LanguageButton({ current, value, onSelect, flag, label }: LanguageButtonProps) {
  const isActive = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={isActive}
      className={cn(
        'flex items-center justify-center gap-1.5 rounded-sm py-1.5 text-xs font-medium',
        'transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <span aria-hidden>{flag}</span>
      <span>{label}</span>
    </button>
  );
}
